import { useQueryClient, type UseQueryOptions } from 'react-query'

import {
  apiPaths,
  queryKeys,
  validateStatus,
  handleError,
} from 'api/kinerja/pengawas-sekolah/config'
import { useMutationApi, useQueryApi } from 'utils/api'
import {
  type TPelaksanaanPerilakuKerja,
  type TPelaksanaanPengembanganKompetensi,
  type TPenilaianPengembanganKompetensiSubmissionBody,
  type TPenilaianPengembanganKompetensiSubmissionResponse,
  type TPraktikKinerjaSummaryRes,
  type TPraktikKinerjaPerencanaanSummary,
  type TDokumenAkuntabilitasResponse,
  type TSubmissionResponse,
  type TPelaksanaanPraktikKinerjaSummaryResponse,
} from 'types/kinerja-ps-pegawai'

import type { TSubmitRatingDiskusiRefleksiTindakLanjutBody } from 'types/kinerja-tim-penilai-ps'
import type { TPraktikKinerjaSurveyPendampingan } from 'types/kinerja-ps-pegawai'
import type { AxiosError } from 'axios'

export function useAPIGetPelaksanaanPerilakuKerjaPS({
  skpPsId,
  ...options
}: UseQueryOptions<TPelaksanaanPerilakuKerja> & { skpPsId: string }) {
  return useQueryApi<TPelaksanaanPerilakuKerja>(
    `${apiPaths.pelaksanaan}/perilaku-kerja/ps/skp-ps/${skpPsId}`,
    {
      queryKey: [queryKeys.API_GET_PELAKSANAAN_PERILAKU_KERJA, skpPsId],
      axiosConfig: {
        validateStatus,
      },
      queryConfig: {
        useErrorBoundary: true,
        ...options,
      },
      errorHandlerFn: handleError,
    }
  )
}

export function useAPIGetPelaksanaanPengembanganKompetensiPS({
  skpPsId,
  ...options
}: UseQueryOptions<TPelaksanaanPengembanganKompetensi> & { skpPsId: string }) {
  return useQueryApi<TPelaksanaanPengembanganKompetensi>(
    `${apiPaths.pelaksanaan}/pengembangan-kompetensi/skp-ps/${skpPsId}`,
    {
      queryKey: [
        queryKeys.API_GET_PELAKSANAAN_PENGEMBANGAN_KOMPETENSI,
        skpPsId,
      ],
      axiosConfig: {
        validateStatus,
      },
      queryConfig: {
        useErrorBoundary: true,
        ...options,
      },
      errorHandlerFn: handleError,
    }
  )
}

export function useAPISubmitPelaksanaanPengembanganKompetensi({ skpPsId }) {
  const queryClient = useQueryClient()

  return useMutationApi<
    TPenilaianPengembanganKompetensiSubmissionResponse,
    AxiosError,
    TPenilaianPengembanganKompetensiSubmissionBody
  >(`${apiPaths.pelaksanaan}/pengembangan-kompetensi/skp-ps/${skpPsId}`, {
    axiosConfig: { method: 'POST' },
    mutationConfig: {
      onSettled() {
        queryClient.invalidateQueries([
          queryKeys.API_GET_PELAKSANAAN_PENGEMBANGAN_KOMPETENSI,
          skpPsId,
        ])
      },
    },
  })
}

export function useAPIGetPraktikKinerjaPerencanaanSummary({
  skpPsId,
  opts,
}: {
  skpPsId: string
  opts?: UseQueryOptions<TPraktikKinerjaPerencanaanSummary>
}) {
  return useQueryApi<TPraktikKinerjaPerencanaanSummary>(
    apiPaths.praktikKinerjaPerencanaanSummary(skpPsId),
    {
      queryKey: [
        queryKeys.API_GET_PRAKTIK_KINERJA_PERENCANAAN_SUMMARY,
        skpPsId,
      ],
      axiosConfig: {
        validateStatus,
      },
      queryConfig: {
        useErrorBoundary: false,
        ...opts,
      },
      errorHandlerFn: handleError,
    }
  )
}

// Get Summary of Praktik Kinerja
export const useApiGetPraktikKinerjaPelaksanaanSummary = ({
  skpPsId,
  opts,
}: {
  skpPsId: string
  opts?: UseQueryOptions<TPraktikKinerjaSummaryRes>
}) => {
  return useQueryApi(
    `${apiPaths.pelaksanaan}/praktik-kinerja/${skpPsId}/summary`,
    {
      axiosConfig: { validateStatus },
      queryConfig: { useErrorBoundary: false, ...opts },
      queryKey: [
        queryKeys.API_GET_PRAKTIK_KINERJA_PELAKSANAAN_SUMMARY,
        skpPsId,
      ],
    }
  )
}

// Get Pelaksanaan Praktik Kinerja all form values
export const useApiGetPraktikKinerjaPelaksanaan = ({
  skpPsId,
  opts,
}: {
  skpPsId: string
  opts?: UseQueryOptions<TPelaksanaanPraktikKinerjaSummaryResponse>
}) => {
  return useQueryApi(apiPaths.pelaksanaanPraktikKinerja(skpPsId), {
    axiosConfig: { validateStatus },
    queryConfig: { useErrorBoundary: false, ...opts },
    queryKey: [queryKeys.API_GET_PRAKTIK_KINERJA_PELAKSANAAN, skpPsId],
  })
}

// Get Dokumen Akuntabilitas
export const useApiGetDokumenAkuntabilitas = ({
  skpPsId,
  opts,
}: {
  skpPsId: string
  opts?: UseQueryOptions<TDokumenAkuntabilitasResponse>
}) => {
  return useQueryApi(`${apiPaths.dokumenAkuntabilitas(skpPsId)}`, {
    axiosConfig: { validateStatus },
    queryConfig: { useErrorBoundary: false, ...opts },
    queryKey: [queryKeys.API_GET_DOKUMEN_AKUNTABILITAS, skpPsId],
  })
}

// Post Dokumen Akuntabilitas
export const useApiPostDokumenAkuntabilitas = ({
  skpPsId,
}: {
  skpPsId: string
}) => {
  const queryClient = useQueryClient()

  return useMutationApi<TSubmissionResponse, AxiosError, {}>(
    `${apiPaths.dokumenAkuntabilitas(skpPsId)}`,
    {
      axiosConfig: { method: 'POST' },
      mutationConfig: {
        onSettled() {
          queryClient.invalidateQueries([
            queryKeys.API_GET_DOKUMEN_AKUNTABILITAS,
            skpPsId,
          ])
        },
      },
    }
  )
}

export const useAPIGetSurveyPendampingan = ({
  skpPsId,
}: {
  skpPsId: string
}) => {
  return useQueryApi<TPraktikKinerjaSurveyPendampingan>(
    `${apiPaths.pelaksanaan}/praktik-kinerja/skp-ps/${skpPsId}/rating/survey-ks`,
    {
      queryKey: [
        queryKeys.API_GET_PRAKTIK_KINERJA_PELAKSANAAN_SURVEY_PENDAMPINGAN,
        skpPsId,
      ],
    }
  )
}

export const useAPIPostSubmitSurveyPendampingan = ({
  skpPsId,
}: {
  skpPsId: string
}) => {
  const queryClient = useQueryClient()

  return useMutationApi<
    TSubmissionResponse,
    AxiosError,
    TSubmitRatingDiskusiRefleksiTindakLanjutBody
  >(
    `${apiPaths.pelaksanaan}/praktik-kinerja/skp-ps/${skpPsId}/rating/survey-ks`,
    {
      axiosConfig: { method: 'POST' },
      mutationConfig: {
        onSettled() {
          queryClient.invalidateQueries([
            queryKeys.API_GET_PRAKTIK_KINERJA_PELAKSANAAN_SURVEY_PENDAMPINGAN,
            skpPsId,
          ])
        },
      },
    }
  )
}
